import i18next, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import commonEn from './locales/en.json';
import commonZh from './locales/zh-Hans.json';

/**
 * i18next setup (orchestrator). The `common` namespace lives here; each feature owns the namespace named after its
 * directory, in `src/features/<area>/locales/{en,zh-Hans}.json`, and registers it once with `registerNamespace`.
 * Language: the local preference, else the browser's (zh* → zh-Hans, anything else → en).
 */
export const SUPPORTED_LANGUAGES = ['en', 'zh-Hans'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export function browserLanguage(languages: readonly string[] = navigator.languages ?? [navigator.language]): Language {
  for (const tag of languages) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('zh')) return 'zh-Hans';
    if (lower.startsWith('en')) return 'en';
  }
  return 'en';
}

const resources: Resource = { en: { common: commonEn }, 'zh-Hans': { common: commonZh } };

void i18next.use(initReactI18next).init({
  resources,
  lng: browserLanguage(),
  fallbackLng: 'en',
  ns: ['common'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Adds a feature's strings (call at module load of the feature's entry). */
export function registerNamespace(ns: string, bundles: { en: Record<string, unknown>; 'zh-Hans': Record<string, unknown> }): void {
  if (!i18next.hasResourceBundle('en', ns)) i18next.addResourceBundle('en', ns, bundles.en, true, true);
  if (!i18next.hasResourceBundle('zh-Hans', ns)) i18next.addResourceBundle('zh-Hans', ns, bundles['zh-Hans'], true, true);
}

export function setLanguage(language: 'system' | Language): void {
  void i18next.changeLanguage(language === 'system' ? browserLanguage() : language);
}

export { i18next };
